import type Stripe from 'stripe';
import type {
  Game,
  TwistGame,
  DailyVariant,
  GameMode,
} from './store/game.svelte';
import { ClassicGame } from './store/classic.svelte';
import { DailyGame } from './store/daily.svelte';

export type Axis = 'row' | 'col' | 'intersection';

export type Coord = readonly [number, number];

export type Match = {
  readonly length: number;
  readonly score: number;
  readonly word: Tile[];
  readonly alpha: string;
  readonly coords: Coord[];
  readonly axis: Axis;
  swaps?: number;
  readonly stars: number;
  readonly intersection?: string;
  readonly intersectingIds: (string | number)[];
  readonly timestamp?: number;
};

export type Freqs<T extends string | number> = Record<T, number>;

export type Tile = {
  id: string;
  letter: string;
  points?: number;
  flipped?: boolean;
  // times the tile has been flipped over (double tap); each flip animates once
  flips?: number;
  multiplier?: number;
  star?: boolean;
  color?: string;
};

export type ClassicTile = Tile & {};

export type DailyTile = Tile & {};

export type TwistTile = Tile & {
  color: string;
};

export type Cell = {
  id: string;
  angle: number; //0 | 90 | 180 | 270;
  tiles: TwistTile[];
};

export type Board<T extends {} = Tile> = T[][];

export type Selection = {
  coords: string[];
  tiles: Set<Tile['id']>;
};

export type Intersections = Record<string, { tile: Tile; coord: Coord }>;

export type WordsByAxis = Record<string, Match[]>;

export type SLeaderboardEntry = {
  id: string;
  userId: string | null;
  gameId: string;
  userName: string;
  score: number;
  bestStreak: number;
  bestWord: Tile[];
  numWords: number;
  numTurns: number;
  date: string;
  rank: number;
  day_rank: number;
  week_rank: number;
  all_rank: number;
  local_rank: number;
  swaps?: number;
};

export type LeaderboardEntry = {
  userId?: string;
  gameId: string;
  name: string;
  score: number;
  bestStreak: number;
  bestWord: Tile[];
  numWords: number;
  turns: number;
  date: string | { seconds: number };
  rank?: number;
  local_rank?: number;
  day_rank?: number;
  week_rank?: number;
  all_rank?: number;
};

export type LeaderboardParams = {
  id?: string;
  score?: string;
  direction: string;
  period: Period;
  rank: number;
};

export type InfiniteLeaderboard = {
  pageParams: LeaderboardParams[];
  pages: LeaderboardEntry[][];
};

export type DailyStats = {
  gamesPlayed: number;
  gamesWon: number;
  lastGamePlayed: string;
  lastGameWon: string;
  currStreak: number;
  bestStreak: number;
  stars: Array<number>;
};

export type LocalDailyStats = DailyStats & {
  stars: number[];
};

export type LocalTwistStats = Omit<DailyStats, 'stars'>;

export type Stats = {
  gamesPlayed: number;
  lastGamePlayed?: string;
  lastGameWon: string;
  winStreak: number;
  bestStreak: number;
  stars: Record<number, number>;
  totalWordsPlayed: number;
  wordScoreFreqs: Record<number, number>;
  wordLengths: Record<number, number>;
};

export type GameType = 'daily' | 'mini' | 'classic' | 'twist';

export type DailyMode = '5x5' | '6x6';

export type GameState<T extends {} = {}> = Partial<Turns> & {
  readonly dims: [number, number];
  // TODO this doesn't exist on classic
  readonly key?: string;
  readonly date?: string;
  startedAt?: number;
  endedAt?: number;
  readonly id: string;
  readonly type: GameType;
  board: Board<T>;
  disabled: boolean;
  finalBoard?: T[][];
  latestWord?: Match;
  words?: Match[];
  numWords: number;
  turn: number;
  durationSeconds?: number;
  /* end */
  streak: number;
  streakLevel: number;
  streakProgress: number;
  streakInterval: number;
  remainingSwaps: number;
  shuffles: number;
  bestStreak: number;
  lost: boolean;
  score: number;
};

export type ClassicGameState = GameState<Tile> & {
  words?: Match[];
  turns?: Turn[];
  wordsDB: Game;
};

export type DailyGameState = GameState<Tile> & {
  readonly key?: string;
  readonly type: 'daily';
  readonly mode: DailyMode;
  readonly date: string;
  readonly completed: boolean;
  stars: number;
  fullClear?: boolean;
};

export type TwistGameState = GameState<Cell> & {
  readonly key?: string;
  readonly type: 'twist';
  readonly mode: DailyMode;
  readonly date: string;
  readonly completed: boolean;
};

type CompletedDailyGameState = DailyGameState & {
  lost: true;
  stars: number;
  fullClear: boolean;
};

export const isTwist = <T extends {}>(game: Game<T>): game is TwistGame =>
  game instanceof TwistGame;

export const isDaily = (game: Game): game is DailyGame =>
  game instanceof DailyGame;

export const isClassic = (game: Game): game is ClassicGame =>
  game instanceof ClassicGame;

export const isComplete = (
  game: DailyGameState,
): game is CompletedDailyGameState => game.lost || game.completed;

export type Turns = {
  turns: Turn[];
  prevWordPointer: number;
  prevTurnPointer: number;
  lastTurnTimestamp: number;
};

export type HighlightColors =
  | 'red'
  | 'orange'
  | 'green'
  | 'blue'
  | 'purple'
  | 'daily-vertical'
  | 'daily-horizontal';

export type Highlighted = Record<string, HighlightColors>;

export type PremiumResponse = {
  premium: boolean;
  subscription?: boolean;
  created_at?: string;
  invoice_status?: Stripe.Invoice.Status;
  subscription_status?: Stripe.Subscription.Status;
};

export type Flagged = {
  word: string;
  userId?: string;
  reason: 'insensitive' | 'obscure';
};

export type Feedback = {
  userId: string | null;
  feedback: string;
};

export type Turn = {
  durationSeconds: number;
  words: number[];
  coords: Coord[];
};

export type Puzzle<T extends object = Tile> = {
  id: string;
  board: Board<T>;
  key?: string;
  date: string;
  mode: GameMode;
  variant: string;
  startedAt?: number;
  completed: boolean;
  words?: Match[];
  turns?: Turn[];
};

export type DailyPageData = {
  puzzle: DailyPuzzle;
};

export type DailyPuzzle = Puzzle<Tile> & {
  // mode: 'daily';
  variant: DailyVariant;
  startedAt?: number;
  stars: number;
  fullClear: boolean;
  words?: Match[];
  finalBoard?: Tile[][];
  turns?: Turn[];
};

export type TwistPuzzle = Puzzle<Cell> & {
  type: 'twist';
  variant: '6x6';
  startedAt?: number;
  completed: boolean;
  finalBoard?: Cell[][];
  turns?: Turn[];
  words: Match[];
};

export type AuthFlowState =
  | 'signup'
  | 'signin'
  | 'signout'
  | 'auth-error'
  | 'verify-email'
  | 'reset-password'
  | undefined;

export type AnalyticsReport = {
  id: string;
  type: GameType;
  userId: string | null;
  userName: string | null;
  date: string;
  score: number;
  durationSeconds: number;
  words: string[];
  turns: Turn[];
  bestStreak: number;
  finalBoard: Tile[][];
  abandoned?: boolean;
};

export type ClickAnalytics = {
  userId: string;
  buttonId: string;
};

export type InfiniteProps = {
  detail: {
    loaded: () => void;
    complete: () => void;
  };
};

export type Period = 'day' | 'week' | 'all' | 'local';

export type DateCell = {
  id: string;
  date: string;
  stars: number;
  fullClear: boolean;
  disabled: boolean;
  empty: boolean;
};

export type CalendarMonth = {
  month: number;
  year: number;
};

// TODO
export type CalendarResponse = Array<{
  date: string;
  data: DateCell;
}>;

export type Word = Pick<Match, 'word' | 'score' | 'swaps'>;

export type StarDistribution = Record<'true' | 'false' | 'total', number>;

export type ArrayElement<T> = T extends readonly unknown[] ? T[0] : never;

export type Modify<T, R> = Omit<T, keyof R> & R;

export type StatsRequest = {
  mode: DailyMode;
  currStreak: number;
  bestStreak: number;
  totalGames: number;
  wonGames: number;
};

export type StreakResponse = {
  current_streak: number;
  best_streak: number;
  total_games: number;
  won_games: number;
};

export type WordSort = 'index' | 'score' | 'alpha' | 'length';

type ApiError = {
  status: number;
  message: string;
};

export const isApiError = (err: unknown): err is ApiError =>
  'status' in (err as ApiError) && 'message' in (err as ApiError);

export type ModalSection = {
  id: string;
  icon?: any;
  desc: string;
};
